"use client";
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import {
  reportConsentBannerShown,
  reportConsentDecision,
  type ConsentDecisionAction,
  type ConsentSurface,
} from "~/lib/consent-analytics";
import {
  buildGaCookieValue,
  buildGaSessionCookieValue,
  gaCookieConfig,
  generateGaClientId,
  generateGaSessionId,
  getGaSessionCookieName,
  getGtagClientId,
} from "~/lib/ga-cookie";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  type ConsentState,
  getBrowserConsentCountryCode,
  parseStoredConsent,
  persistConsent,
  shouldShowConsentBanner,
} from "~/lib/consent-region";
import { getFirstPartyCookieDomain } from "~/lib/cookie-domain";

// SharedComponents.tsx
import { Switch } from "~/components/ui/switch";
import { Label } from "~/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";

import React, { Suspense } from "react";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Cookie, ListChecks, Save } from "lucide-react";
import { cn } from "~/lib/utils";
export interface CookiePreferences {
  adConsent: boolean;
  analyticsConsent: boolean;
}

type UseCookieConsentOptions = {
  countryCode?: string | null;
  initialConsentValue?: string | null;
  surface?: ConsentSurface;
};

function getGaCookieDomain(): string {
  return (
    getFirstPartyCookieDomain(window.location.hostname) ??
    window.location.hostname
  );
}

function writeCookie(
  name: string,
  value: string,
  maxAge: number,
  domain?: string,
) {
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  const domainStr = domain ? `;domain=${domain}` : "";
  document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax${secure}${domainStr}`;
}

function ensureAnalyticsCookies() {
  const domain = getGaCookieDomain();
  const sessionCookieName = getGaSessionCookieName(
    gaCookieConfig.measurementId,
  );
  if (!document.cookie.includes(`${sessionCookieName}=`)) {
    writeCookie(
      sessionCookieName,
      buildGaSessionCookieValue(generateGaSessionId()),
      gaCookieConfig.sessionMaxAgeSeconds,
      domain,
    );
  }

  if (document.cookie.includes("_ga=")) {
    return;
  }

  // Set a fallback _ga cookie immediately on the root domain (matching
  // gtag's cookie_domain: 'auto') so that when gtag processes the
  // consent_update event, it sees an existing cookie and doesn't
  // create a duplicate.
  const fallbackClientId = generateGaClientId();
  writeCookie(
    "_ga",
    buildGaCookieValue(fallbackClientId),
    gaCookieConfig.maxAgeSeconds,
    domain,
  );

  // Then try to read gtag's pre-consent ephemeral client_id and overwrite.
  // Before consent, gtag fires session_start with an ephemeral cid that
  // captures the traffic source (gclid, referrer). Persisting THAT cid
  // ensures the purchase event joins to the same GA4 session.
  getGtagClientId().then((gtagClientId) => {
    if (gtagClientId && gtagClientId !== fallbackClientId) {
      writeCookie(
        "_ga",
        buildGaCookieValue(gtagClientId),
        gaCookieConfig.maxAgeSeconds,
        domain,
      );
    }
  });
}

export const useCookieConsent = (options?: UseCookieConsentOptions) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    adConsent: false,
    analyticsConsent: false,
  });

  useEffect(() => {
    const countryCode =
      options?.countryCode ?? getBrowserConsentCountryCode() ?? null;
    const localConsent = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    const consentValue = localConsent ?? options?.initialConsentValue ?? null;
    const parsedConsent = parseStoredConsent(consentValue);

    if (parsedConsent) {
      setPreferences(parsedConsent);
      if (!localConsent) {
        persistConsent(parsedConsent, { notify: false });
      } else {
        writeCookie(
          COOKIE_CONSENT_STORAGE_KEY,
          localConsent,
          60 * 60 * 24 * 365,
        );
      }
      if (parsedConsent.analyticsConsent) {
        ensureAnalyticsCookies();
      }
    } else {
      setIsVisible(
        shouldShowConsentBanner({
          countryCode,
          consentValue: options?.initialConsentValue,
        }),
      );
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== COOKIE_CONSENT_STORAGE_KEY) {
        return;
      }

      const nextConsent = parseStoredConsent(event.newValue);
      if (nextConsent) {
        setPreferences(nextConsent);
        setIsVisible(false);
      } else {
        setIsVisible(
          shouldShowConsentBanner({
            countryCode,
            consentValue: options?.initialConsentValue,
          }),
        );
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [options?.countryCode, options?.initialConsentValue]);

  const handleAcceptAll = () => {
    const allAccepted = {
      adConsent: true,
      analyticsConsent: true,
    };
    savePreferences(allAccepted, "accept_all");
  };

  const handleSavePreferences = () => {
    savePreferences(preferences, "save_preferences");
  };

  const handleTogglePreference = (key: keyof CookiePreferences) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const savePreferences = (
    newPreferences: ConsentState,
    action: ConsentDecisionAction,
  ) => {
    reportConsentDecision(action, newPreferences, options?.surface ?? "banner");
    persistConsent(newPreferences);
    if (newPreferences.analyticsConsent) {
      ensureAnalyticsCookies();
    }
    setPreferences(newPreferences);
    setIsVisible(false);
  };

  const disableAll = () => {
    const allDisabled = {
      adConsent: false,
      analyticsConsent: false,
    };
    savePreferences(allDisabled, "reject_optional");
  };

  return {
    isVisible,
    showPreferences,
    preferences,
    handleAcceptAll,
    handleSavePreferences,
    handleTogglePreference,
    setShowPreferences,
    disableAll,
  };
};

export const PreferenceToggle = ({
  id,
  label,
  description,
  checked,
  onChange,
}: {
  id: keyof CookiePreferences;
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <div className="flex items-center justify-between">
    <div>
      <Label htmlFor={id} className="text-base font-medium">
        {label}
      </Label>
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
    <Switch id={id} checked={checked} onCheckedChange={onChange} />
  </div>
);

export const EssentialCookiesAccordion = () => (
  <Accordion type="single" collapsible className="w-full">
    <AccordionItem value="essential-cookies">
      <AccordionTrigger>Neophodni kolačići</AccordionTrigger>
      <AccordionContent>
        <p className="mb-2">
          Ovi kolačići su neophodni za rad našeg sajta i ne mogu se isključiti.
          Oni obično uključuju:
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>Autentifikacijske tokene za sigurno prijavljivanje</li>
          <li>Sesijske tokene za pamćenje tvoje trenutne sesije</li>
          <li>
            CSRF tokene za zaštitu od napada i zloupotreba tvojih podataka
          </li>
          <li>Kolačiće za pamćenje stanja interfejsa</li>
        </ul>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

export function CookieConsent({
  countryCode,
  initialConsentValue,
}: UseCookieConsentOptions) {
  const {
    preferences,
    showPreferences,
    handleAcceptAll,
    handleSavePreferences,
    handleTogglePreference,
    setShowPreferences,
    isVisible,
    disableAll,
  } = useCookieConsent({ countryCode, initialConsentValue });

  if (!isVisible) return null;

  return (
    <Card className="mx-4 max-w-xl shadow-md sm:mx-auto">
      <CardContent className="p-3 sm:p-4">
        <div className="mb-3 hidden items-center sm:flex">
          <Cookie className="mr-2 h-5 w-5" />
          <p className="text-base font-bold">
            Stalo nam je do tvoje privatnosti
          </p>
        </div>
        <p className="mb-3 text-xs">
          <Cookie className="mr-1 inline h-3.5 w-3.5 align-text-bottom sm:hidden" />
          Koristimo neophodne kolačiće za prijavu, sigurnost i rad platforme.
          Analitičke i marketing kolačiće koristimo samo u skladu sa tvojim
          izborom.{" "}
          {!showPreferences && (
            <button
              type="button"
              onClick={() => setShowPreferences(true)}
              className="underline underline-offset-2 sm:hidden"
            >
              Podešavanja
            </button>
          )}
        </p>
        {showPreferences ? (
          <div className="space-y-4">
            <PreferenceToggle
              id="adConsent"
              label="Marketing"
              description="Pomaže nam da upravljamo marketinškim kampanjama i vidimo koliko dobro rade."
              checked={preferences.adConsent}
              onChange={() => handleTogglePreference("adConsent")}
            />
            <PreferenceToggle
              id="analyticsConsent"
              label="Analitika"
              description="Pomaže nam da razumemo korišćenje sajta, greške i performanse. Može uključivati tehničke identifikatore."
              checked={preferences.analyticsConsent}
              onChange={() => handleTogglePreference("analyticsConsent")}
            />
            <EssentialCookiesAccordion />
            <div className="mt-6 flex flex-col space-y-2 sm:flex-row sm:justify-end sm:space-y-0 sm:space-x-2">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setShowPreferences(false)}
              >
                Nazad
              </Button>
              <Button
                className="w-full sm:w-auto"
                onClick={handleSavePreferences}
                icon={Save}
              >
                Sačuvaj podešavanja
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreferences(true)}
              className="hidden sm:inline-flex"
            >
              Podešavanja
            </Button>
            <Button variant="outline" size="sm" onClick={disableAll}>
              Odbij neobavezne
            </Button>
            <Button size="sm" onClick={handleAcceptAll} icon={ListChecks}>
              Prihvati sve
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function CookieConsentBanner({
  countryCode,
  initialConsentValue,
}: UseCookieConsentOptions) {
  return (
    <Suspense fallback={null}>
      <CookieConsentBannerContent
        countryCode={countryCode}
        initialConsentValue={initialConsentValue}
      />
    </Suspense>
  );
}

function CookieConsentBannerContent({
  countryCode,
  initialConsentValue,
}: UseCookieConsentOptions) {
  // This hook instance only tracks visibility; the CookieConsent child owns
  // its own instance with the action handlers. Consent analytics stay
  // single-fire because banner_shown reports here (ref-guarded) and
  // decisions report only from the child's savePreferences.
  const { isVisible } = useCookieConsent({ countryCode, initialConsentValue });
  const pathname = usePathname();
  const hasReportedShown = useRef(false);

  useEffect(() => {
    if (isVisible && !hasReportedShown.current) {
      hasReportedShown.current = true;
      reportConsentBannerShown("banner");
    }
  }, [isVisible]);
  const isAssistantPage =
    pathname === "/asistent" || pathname.startsWith("/app/asistent");
  const isLoginPage = pathname === "/app/login";
  // On phones the banner sits above the sticky booking and quiz CTAs
  // (bottom-0, up to ~125px tall) instead of covering the page headline.
  // Pages with their own bottom chrome override the offset.
  let positionClass = "bottom-32 sm:bottom-4";
  if (isAssistantPage) {
    positionClass = "bottom-64 sm:bottom-4";
  } else if (isLoginPage) {
    positionClass = "bottom-4";
  }

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed right-0 left-0 z-50 max-h-[calc(100dvh-4rem)] overflow-y-auto sm:right-auto sm:left-4 sm:max-w-md",
        positionClass,
      )}
    >
      <CookieConsent
        countryCode={countryCode}
        initialConsentValue={initialConsentValue}
      />
    </div>
  );
}

export function CookieManagementPage() {
  const { preferences, handleTogglePreference, handleSavePreferences } =
    useCookieConsent({ surface: "management_page" });

  return (
    <div className="container mx-auto max-w-3xl py-8">
      <CardHeader>
        <CardTitle className="flex items-center text-2xl">
          <Cookie className="mr-2 h-6 w-6" />
          Upravljanje kolačićima
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground mb-6">
          Ovde možeš pregledati i promeniti podešavanja kolačića. Tvoj izbor će
          važiti za buduće posete sa ovog uređaja i pregledača.
        </p>

        <div className="space-y-6">
          <PreferenceToggle
            id="adConsent"
            label="Marketing"
            description="Pomaže nam da upravljamo marketinškim kampanjama i vidimo koliko dobro rade."
            checked={preferences.adConsent}
            onChange={() => handleTogglePreference("adConsent")}
          />
          <PreferenceToggle
            id="analyticsConsent"
            label="Analitika"
            description="Pomaže nam da razumemo korišćenje sajta, greške i performanse. Može uključivati tehničke identifikatore."
            checked={preferences.analyticsConsent}
            onChange={() => handleTogglePreference("analyticsConsent")}
          />

          <EssentialCookiesAccordion />

          <div className="flex justify-end">
            <Button onClick={handleSavePreferences} icon={Save}>
              Sačuvaj podešavanja
            </Button>
          </div>
        </div>
      </CardContent>
    </div>
  );
}
